/** Classic Klondike rules. Every action returns a new state; the last card in a pile is its top. */
export const SUITS = ['spades', 'hearts', 'clubs', 'diamonds'];

export const isRed = card => card.suit === 'hearts' || card.suit === 'diamonds';

export function createGame({ drawCount = 1, random = Math.random } = {}) {
    const deck = SUITS.flatMap(suit => Array.from({ length: 13 }, (_, index) => ({
        id: `${suit}-${index + 1}`, suit, rank: index + 1, faceUp: false,
    })));
    for (let i = deck.length - 1; i > 0; i -= 1) {
        const j = Math.floor(random() * (i + 1));
        [deck[i], deck[j]] = [deck[j], deck[i]];
    }
    const tableau = Array.from({ length: 7 }, () => []);
    for (let row = 0; row < 7; row += 1) {
        for (let column = row; column < 7; column += 1) {
            tableau[column].push({ ...deck.pop(), faceUp: row === column });
        }
    }
    const count = drawCount === 3 ? 3 : 1;
    return {
        game: count === 3 ? 'solitaireDrawThree' : 'solitaire', drawCount: count,
        stock: deck, waste: [], tableau, foundations: SUITS.map(() => []),
        status: 'ready', moves: 0, history: [],
    };
}

function pileAt(state, location) {
    if (!location) return null;
    if (location.pile === 'waste') return state.waste;
    if (location.pile === 'tableau' || location.pile === 'foundations') {
        return state[location.pile][location.index] || null;
    }
    return null;
}

export function movableCards(state, source) {
    const pile = pileAt(state, source);
    if (!pile || state.status === 'won') return [];
    const index = source.cardIndex;
    if (!Number.isInteger(index) || index < 0 || index >= pile.length) return [];
    if (source.pile !== 'tableau' && index !== pile.length - 1) return [];
    const cards = pile.slice(index);
    if (cards.some((card, i) => !card.faceUp || (i > 0
        && (cards[i - 1].rank !== card.rank + 1 || isRed(cards[i - 1]) === isRed(card))))) return [];
    return cards;
}

export function canMove(state, source, destination) {
    if (!source || !destination || (source.pile === destination.pile && source.index === destination.index)) {
        return false;
    }
    const cards = movableCards(state, source);
    const target = pileAt(state, destination);
    if (!cards.length || !target) return false;
    const card = cards[0];
    if (destination.pile === 'foundations') {
        return cards.length === 1 && card.suit === SUITS[destination.index] && card.rank === target.length + 1;
    }
    if (destination.pile !== 'tableau') return false;
    const top = target[target.length - 1];
    return top ? top.faceUp && top.rank === card.rank + 1 && isRed(top) !== isRed(card) : card.rank === 13;
}

function nextState(state) {
    const { history, ...snapshot } = state;
    return {
        ...snapshot, status: 'playing', moves: state.moves + 1, history: [...history, snapshot],
        stock: [...state.stock], waste: [...state.waste],
        tableau: state.tableau.map(pile => [...pile]), foundations: state.foundations.map(pile => [...pile]),
    };
}

export function draw(state) {
    if (state.status === 'won' || (!state.stock.length && !state.waste.length)) return state;
    const next = nextState(state);
    if (next.stock.length) {
        const count = Math.min(next.drawCount, next.stock.length);
        for (let i = 0; i < count; i += 1) next.waste.push({ ...next.stock.pop(), faceUp: true });
    } else {
        next.stock = next.waste.reverse().map(card => ({ ...card, faceUp: false }));
        next.waste = [];
    }
    return next;
}

export function move(state, source, destination) {
    if (!canMove(state, source, destination)) return state;
    const next = nextState(state);
    const from = pileAt(next, source);
    const cards = from.splice(source.cardIndex);
    pileAt(next, destination).push(...cards);
    if (source.pile === 'tableau' && from.length) {
        from[from.length - 1] = { ...from[from.length - 1], faceUp: true };
    }
    if (next.foundations.every(pile => pile.length === 13)) next.status = 'won';
    return next;
}

export function undo(state) {
    if (!state.history.length || state.status === 'won') return state;
    return { ...state.history[state.history.length - 1], history: state.history.slice(0, -1) };
}
